import { type ReactNode } from 'react';
import { ClerkProvider as BaseClerkProvider } from '@clerk/react';
import { nlNL } from '@clerk/localizations';

const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env ?? {};
const publishableKey = env.VITE_CLERK_PUBLISHABLE_KEY ?? '';
const devBypass = env.VITE_DEV_BYPASS_AUTH === '1';

/** Auth-route paths — used by App.tsx for route matching & redirect targets. */
export const clerkSignInUrl = env.VITE_CLERK_SIGN_IN_URL ?? '/sign-in';
export const clerkSignUpUrl = env.VITE_CLERK_SIGN_UP_URL ?? '/sign-up';
// Where Clerk lands authenticated users after sign-in / sign-up (no redirect_url
// in the URL). `/` is correct: App.tsx routes authenticated users to the
// overview page, unauthenticated users to /sign-in.
/** Used by App.tsx for `<SignIn>` / `<SignUp>` so component props match provider-level fallbacks. */
export const clerkAfterSignInUrl = env.VITE_CLERK_AFTER_SIGN_IN_URL ?? '/';
export const clerkAfterSignUpUrl = env.VITE_CLERK_AFTER_SIGN_UP_URL ?? '/';

export function ClerkProvider({ children }: { children: ReactNode }) {
  if (devBypass) {
    return <>{children}</>;
  }

  if (!publishableKey.trim()) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', padding: 32 }}>
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <h1 style={{ fontSize: 24, marginBottom: 12 }}>Configuration missing</h1>
          <p style={{ color: '#666' }}>
            <code>VITE_CLERK_PUBLISHABLE_KEY</code> is not set. Add it to your <code>.env</code> file or Vercel environment variables to enable authentication.
          </p>
        </div>
      </div>
    );
  }

  // Wire the production custom domain (clerk.stagery.ai) only for live keys.
  // Dev/test instances must keep Clerk's default frontend API host.
  const isProdInstance = publishableKey.startsWith('pk_live_');
  const customDomain = isProdInstance ? 'clerk.stagery.ai' : undefined;

  return (
    <BaseClerkProvider
      publishableKey={publishableKey}
      localization={{
        ...nlNL,
        signIn: {
          ...nlNL.signIn,
          start: {
            ...nlNL.signIn?.start,
            // BUG-APP-001: 'Wachtwoord vergeten?' appears on the password step (multi-step flow)
            actionText: 'Wachtwoord vergeten?',
          },
        },
        // BUG-APP-003: Clear success state on the verify-email-address step
        // Clerk's default NL text is minimal — override with explicit instructions
        signUp: {
          ...nlNL.signUp,
          emailCode: {
            ...nlNL.signUp?.emailCode,
            title: 'Controleer je inbox',
            subtitle: 'We hebben een verificatielink gestuurd naar {{identifier}}. Klik op de link om je account te activeren.',
            resendButton: 'Opnieuw versturen',
          },
          emailLink: {
            ...nlNL.signUp?.emailLink,
            title: 'Controleer je inbox',
            subtitle: 'We hebben een verificatielink gestuurd naar {{identifier}}. Klik op de link in de e-mail om je account te activeren.',
            resendButton: 'Opnieuw versturen',
          },
        },
        formFieldInputPlaceholder__password: 'Wachtwoord',
      }}
      domain={customDomain}
      signInUrl={clerkSignInUrl}
      signUpUrl={clerkSignUpUrl}
      signInFallbackRedirectUrl={clerkAfterSignInUrl}
      signUpFallbackRedirectUrl={clerkAfterSignUpUrl}
      signInForceRedirectUrl={clerkAfterSignInUrl}
      signUpForceRedirectUrl={clerkAfterSignUpUrl}
    >
      {children}
    </BaseClerkProvider>
  );
}
